import React, { useState, useEffect } from "react";
import useStyles from "./style";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import "jspdf-autotable";
import io from "socket.io-client";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import _ from "lodash";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { useIntl } from "react-intl";
import { CUSTOM_CONSTANTS } from "Constants";

function formatDate(inputDate) {
  if (!inputDate) return "--";
  const date = new Date(inputDate);
  if (Number.isNaN(date.getTime())) return "--";
  const options = { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: true };
  const formatted = new Intl.DateTimeFormat("en-US", options).format(date);
  const [datePart, timePart] = formatted.split(", ");
  const [month, day, year] = datePart.split("/");
  return `${year}-${month}-${day} ${timePart}`;
}

function convertToISO(inputDate) {
  if (!inputDate) return "";
  const date = new Date(inputDate);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().replace("Z", "+00:00");
}

function calculateSno(pageIndex, pageSize, index) {
  return (pageIndex - 1) * pageSize + index + 1;
}

const TRIP_STATUSES = ["SEARCHING", "CANCELLED", "COMPLETED", "SCHEDULED"];
const BOOKING_TYPES = ["MOBILE APP", "WEB BOOKING", "STREET RIDE"];

const TriplistPassenger = () => {
  const classes = useStyles();
  const intl = useIntl();

  const userId =
    useSelector((state) => state?.AuthReducer?.userid || "") ||
    localStorage.getItem("userid") ||
    "";

  const [rows, setRows] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filtervalues, setFiltervalues] = useState({});

  useEffect(() => {
    const socket = io(CUSTOM_CONSTANTS.API_BASE_URL, {
      query: { authtoken: `Bearer ${localStorage.getItem("token")}` },
      transports: ["websocket"],
      path: "/socket.io/",
    });

    const interval = setInterval(() => {
      socket.emit("getAllUserRideRequests", { page, limit: pageSize, filtervalues, userId });
    }, 5000);

    socket.on("tripListUpdated", (data) => {
      const list = Array.isArray(data) ? data : data ? [data] : [];
      const payload = list[0] ?? data;

      if (payload?.success) {
        setTotalRecords(payload?.data?.totalRecords || 0);
        setRows(Array.isArray(payload?.data?.data?.triplists) ? payload.data.data.triplists : []);
        setServicesData(Array.isArray(payload?.data?.data?.serviceTypeDetails) ? payload.data.data.serviceTypeDetails : []);
      } else {
        setRows([]);
        setTotalRecords(0);
      }
    });

    return () => {
      clearInterval(interval);
      socket.off("tripListUpdated");
      socket.disconnect();
    };
  }, [page, pageSize, filtervalues, userId]);

  const dataRows = _.map(rows, (data, index) => {
    const passenger = data?.users_ride_requests_user_idTousers || {};
    const driver = data?.driver_rides?.users || {};

    return {
      id: data?.id || index,
      sno: calculateSno(page, pageSize, index),
      bookingId: data?.booking_id || "--",
      requestedAt: formatDate(data?.requested_at),
      startedAt: formatDate(data?.started_at),
      completedAt: formatDate(data?.completed_at),
      username: `${passenger?.first_name || ""} ${passenger?.last_name || ""}`.trim() || data?.user_name || "--",
      drivername: driver?.first_name
        ? `${driver.first_name} ${driver.last_name || ""}`.trim()
        : intl.formatMessage({ id: "dashboard.tripList.unassignedDriver" }),
      driverPhone: driver?.phone || "--",
      vehicleNo: data?.driver_rides?.vehicle_number || "--",
      servicename: data?.service_name || "--",
      pickuplocation: data?.pickup_address || "--",
      droplocation: data?.drop_address || "--",
      distance: `${parseFloat(data?.trip_distance ?? 0).toFixed(2)} ${data?.trip_distance_unit || "km"}`,
      fare: `${data?.estimated_fare ?? 0} ${CUSTOM_CONSTANTS.BASE_CURRENCY}`,
      bookingType: data?.booking_type || "--",
      paymentMode: data?.payment_mode || "--",
      cancelledBy: data?.cancelled_by || "--",
      status: data?.trip_status || "--",
    };
  });

  const columns = [
    { field: "requestedAt", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.requestedAt" }), flex: 1.5, minWidth: 150 },
    { field: "startedAt", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.startedAt" }), flex: 1.5, minWidth: 150 },
    { field: "completedAt", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.completedAt" }), flex: 1.5, minWidth: 150 },
    {
      field: "drivername",
      headerName: intl.formatMessage({ id: "dashboard.tripList.columns.driver" }),
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {params.row.drivername}
          </Typography>
          {params.row.vehicleNo !== "--" && (
            <Typography variant="caption" color="text.secondary">
              {params.row.vehicleNo}
            </Typography>
          )}
        </Box>
      ),
    },
    { field: "servicename", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.service" }), flex: 1.2, minWidth: 110 },
    { field: "pickuplocation", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.pickup" }), flex: 2, minWidth: 180 },
    { field: "droplocation", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.dropoff" }), flex: 2, minWidth: 180 },
    { field: "distance", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.distance" }), flex: 1, minWidth: 100 },
    {
      field: "fare",
      headerName: intl.formatMessage({ id: "dashboard.tripList.columns.fare" }),
      flex: 1.1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: "#11823b" }}>
          {params.value}
        </Typography>
      ),
    },
    { field: "paymentMode", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.payment" }), flex: 1, minWidth: 100 },
    { field: "bookingType", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.type" }), flex: 1.2, minWidth: 110 },
    { field: "cancelledBy", headerName: intl.formatMessage({ id: "dashboard.tripList.columns.cancelledBy" }), flex: 1, minWidth: 100 },
    {
      field: "status",
      headerName: intl.formatMessage({ id: "dashboard.tripList.columns.status" }),
      flex: 1.2,
      minWidth: 120,
      renderCell: (params) => {
        const colorMap = {
          COMPLETED: "#11823b",
          CANCELLED: "#d32f2f",
          CANCELED: "#d32f2f",
          SEARCHING: "#f57c00",
          ARRIVED: "#7b1fa2",
          RIDING: "#1565c0",
        };

        return (
          <Typography variant="body2" sx={{ color: colorMap[params.value] || "#555", fontWeight: 700 }}>
            {params.value}
          </Typography>
        );
      },
    },
  ];

  const formik = useFormik({
    initialValues: { tripStatus: "", serviceTypeId: "", bookingType: "", fromdate: "", todate: "" },
    validationSchema: yup.object({
      fromdate: yup.string().nullable(),
      todate: yup.string().nullable(),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      setFiltervalues({
        ...values,
        fromdate: convertToISO(values.fromdate),
        todate: convertToISO(values.todate),
      });
      setPage(1);
    },
  });

  const handleReset = () => {
    formik.resetForm();
    setFiltervalues({});
    setPage(1);
  };

  const totalTrips = totalRecords || dataRows.length;
  const confirmedTrips = dataRows.filter((row) => String(row.status).toUpperCase() === "COMPLETED").length;
  const waitingTrips = dataRows.filter((row) => ["SEARCHING", "SCHEDULED"].includes(String(row.status).toUpperCase())).length;
  const revenueTotal = dataRows.reduce((sum, row) => {
    const value = Number.parseFloat(String(row.fare || "0").replace(/[^\d.-]/g, ""));
    return sum + (Number.isNaN(value) ? 0 : value);
  }, 0);
  const formatMoney = (value) => new Intl.NumberFormat("fr-FR").format(Math.round(value));

  const metricItems = [
    {
      label: intl.formatMessage({ id: "dashboard.tripList.metrics.found.label" }),
      value: totalTrips,
      meta: intl.formatMessage({ id: "dashboard.tripList.metrics.found.meta" }),
      tone: "summary",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 17h10v2H7z" fill="currentColor" />
          <path d="M8 3h8v2H8zM6 5h12v3H6z" fill="currentColor" opacity="0.9" />
          <path d="M7 8h10l1 10H6L7 8zm2 2-.6 6h7.2L15 10H9z" fill="currentColor" />
        </svg>
      ),
    },
    {
      label: intl.formatMessage({ id: "dashboard.tripList.metrics.confirmed.label" }),
      value: confirmedTrips,
      meta: `${totalTrips ? ((confirmedTrips / totalTrips) * 100).toFixed(1) : "0.0"}%`,
      tone: "success",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2a10 10 0 1 0 10 10A10.01 10.01 0 0 0 12 2zm-1 13.2-3.2-3.2 1.4-1.4L11 12.4l4.8-4.8 1.4 1.4z" fill="currentColor" />
        </svg>
      ),
    },
    {
      label: intl.formatMessage({ id: "dashboard.tripList.metrics.waiting.label" }),
      value: waitingTrips,
      meta: `${totalTrips ? ((waitingTrips / totalTrips) * 100).toFixed(1) : "0.0"}%`,
      tone: "warning",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3a9 9 0 1 0 9 9 9 9 0 0 0-9-9zm1 9.4V7h-2v6l5 3 1-1.7z" fill="currentColor" />
        </svg>
      ),
    },
    {
      label: intl.formatMessage({ id: "dashboard.tripList.metrics.seats.label" }),
      value: "8/40",
      meta: intl.formatMessage({ id: "dashboard.tripList.metrics.seats.meta" }),
      tone: "seat",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 10a2 2 0 1 1 2-2 2 2 0 0 1-2 2zm12 0a2 2 0 1 1 2-2 2 2 0 0 1-2 2zM5 20v-4h14v4H5zm2-10h10a2 2 0 0 1 2 2v3H5v-3a2 2 0 0 1 2-2z" fill="currentColor" />
        </svg>
      ),
    },
    {
      label: intl.formatMessage({ id: "dashboard.tripList.metrics.revenue.label" }),
      value: `${formatMoney(revenueTotal)} XAF`,
      meta: intl.formatMessage({ id: "dashboard.tripList.metrics.revenue.meta" }),
      tone: "revenue",
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 14.9V18h-2v-1.1a4.3 4.3 0 0 1-3.4-3.1l1.9-.5a2.6 2.6 0 0 0 2.6 1.8c1.2 0 2.1-.5 2.1-1.4 0-.8-.6-1.2-2.4-1.6-2.6-.6-3.9-1.5-3.9-3.4a3.4 3.4 0 0 1 3.1-3.3V5h2v1.1A4.1 4.1 0 0 1 16.7 8l-1.8.6a2.2 2.2 0 0 0-2.1-1.2c-1 0-1.8.5-1.8 1.3s.7 1.2 2.5 1.6c2.4.6 3.8 1.4 3.8 3.4A3.4 3.4 0 0 1 13 16.9z" fill="currentColor" />
        </svg>
      ),
    },
  ];

  return (
    <div className={classes.pages}>
      <Grid container className={classes.root}>
        <Grid item xs={12}>
          <Box
            sx={{
              mx: 1.5,
              mt: 1.5,
              mb: 2,
              borderRadius: "18px",
              background: "linear-gradient(90deg, #0c6d3b 0%, #0f7a3d 40%, #0d5f2a 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              minHeight: { xs: 220, md: 250 },
              px: { xs: 2.5, md: 4 },
              py: { xs: 3, md: 3.5 },
              boxShadow: "0 18px 45px rgba(12, 109, 59, 0.28)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at 72% 8%, rgba(255,255,255,0.08), transparent 18%), radial-gradient(circle at 50% 55%, rgba(255,255,255,0.06), transparent 10%), radial-gradient(circle at 92% 0%, rgba(255,255,255,0.08), transparent 16%)",
                pointerEvents: "none",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                alignItems: { xs: "flex-start", lg: "center" },
                justifyContent: "space-between",
                gap: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5, flex: 1 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
                  }}
                >
                  <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true" fill="none">
                    <path d="M4 15.5h16v2H4z" fill="white" />
                    <path d="M6.5 11h11l1.6 4h-14z" fill="white" opacity="0.95" />
                    <path d="M7.3 7h9.4l1.3 4H6z" fill="white" opacity="0.78" />
                    <path d="M7 17.8a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8zM17 17.8a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8z" fill="white" />
                  </svg>
                </Box>
                <Box sx={{ pt: 0.5 }}>
                  <Typography sx={{ fontSize: { xs: "2rem", md: "2.9rem" }, lineHeight: 1.05, fontWeight: 800 }}>
                    {intl.formatMessage({ id: "dashboard.tripList.header.title" })}
                  </Typography>
                  <Typography sx={{ mt: 1, fontSize: { xs: 14, md: 16 }, color: "rgba(255,255,255,0.9)" }}>
                    {intl.formatMessage({ id: "dashboard.tripList.header.subtitle" })}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  minWidth: { xs: "100%", sm: 280 },
                  maxWidth: 320,
                  alignSelf: { xs: "stretch", lg: "flex-start" },
                  borderRadius: "14px",
                  border: "1px solid rgba(255,255,255,0.28)",
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(4px)",
                  px: 2,
                  py: 1.8,
                }}
              >
                <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.82)" }}>
                  {intl.formatMessage({ id: "dashboard.tripList.header.periodLabel" })}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mt: 0.5 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
                    03 Jul 2026 - 09 Jul 2026
                  </Typography>
                  <Typography sx={{ fontSize: 18, color: "#fff", lineHeight: 1 }}>▾</Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                position: "relative",
                mt: { xs: 3, md: 4 },
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(5, minmax(0, 1fr))" },
                gap: 0,
                borderTop: "1px solid rgba(255,255,255,0.16)",
              }}
            >
              {metricItems.map((item, index) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.8,
                    px: { xs: 0, xl: index === 0 ? 0 : 3 },
                    py: 2,
                    borderRight:
                      index < metricItems.length - 1
                        ? { xs: "none", xl: "1px solid rgba(255,255,255,0.15)" }
                        : "none",
                  }}
                >
                  <Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      background:
                        item.tone === "warning"
                          ? "rgba(255, 170, 0, 0.18)"
                          : item.tone === "seat"
                          ? "rgba(88, 189, 107, 0.2)"
                          : item.tone === "revenue"
                          ? "rgba(255, 214, 102, 0.18)"
                          : item.tone === "success"
                          ? "rgba(88, 189, 107, 0.2)"
                          : "rgba(255,255,255,0.12)",
                      color: "#fff",
                    }}
                  >
                    <Box sx={{ width: 24, height: 24, color: "#fff" }}>{item.icon}</Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.94)" }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ mt: 0.3, fontSize: item.label === intl.formatMessage({ id: "dashboard.tripList.metrics.seats.label" }) ? 24 : 28, lineHeight: 1, fontWeight: 800, color: "#fff" }}>
                      {item.value}
                    </Typography>
                    <Typography sx={{ mt: 0.6, fontSize: 13, color: "rgba(255,255,255,0.82)" }}>
                      {item.meta}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid container item xs={12} className={classes.utilities} spacing={1} sx={{ px: 2 }}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              name="tripStatus"
              select
              fullWidth
              size="small"
              variant="outlined"
              value={formik.values.tripStatus}
              onChange={formik.handleChange}
              SelectProps={{ displayEmpty: true, renderValue: (v) => v || intl.formatMessage({ id: "dashboard.tripList.filters.allStatuses" }) }}
            >
              <MenuItem value="">{intl.formatMessage({ id: "dashboard.tripList.filters.allStatuses" })}</MenuItem>
              {TRIP_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              name="serviceTypeId"
              select
              fullWidth
              size="small"
              variant="outlined"
              value={formik.values.serviceTypeId}
              onChange={formik.handleChange}
              SelectProps={{ displayEmpty: true, renderValue: (v) => v || intl.formatMessage({ id: "dashboard.tripList.filters.allServices" }) }}
            >
              <MenuItem value="">{intl.formatMessage({ id: "dashboard.tripList.filters.allServices" })}</MenuItem>
              {servicesData.map((s) => <MenuItem key={s._id} value={s._id}>{s.serviceName}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              name="bookingType"
              select
              fullWidth
              size="small"
              variant="outlined"
              value={formik.values.bookingType}
              onChange={formik.handleChange}
              SelectProps={{ displayEmpty: true, renderValue: (v) => v || intl.formatMessage({ id: "dashboard.tripList.filters.bookingTypeLabel" }) }}
            >
              <MenuItem value="">{intl.formatMessage({ id: "dashboard.tripList.filters.bookingTypeLabel" })}</MenuItem>
              {BOOKING_TYPES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={intl.formatMessage({ id: "dashboard.tripList.filters.startDate" })}
                value={formik.values.fromdate || null}
                onChange={(v) => formik.setFieldValue("fromdate", v ? moment(v).format("MM/DD/YYYY HH:mm:ss") : "")}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={intl.formatMessage({ id: "dashboard.tripList.filters.endDate" })}
                value={formik.values.todate || null}
                onChange={(v) => formik.setFieldValue("todate", v ? moment(v).format("MM/DD/YYYY HH:mm:ss") : "")}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={6} sm={3} md={1}>
            <Button variant="contained" fullWidth onClick={formik.handleSubmit} sx={{ height: 40 }}>
              {intl.formatMessage({ id: "dashboard.tripList.filters.apply" })}
            </Button>
          </Grid>

          <Grid item xs={6} sm={3} md={1}>
            <Button variant="outlined" fullWidth onClick={handleReset} sx={{ height: 40 }}>
              {intl.formatMessage({ id: "dashboard.tripList.filters.reset" })}
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <section className={classes.dataGridTable}>
            <DataGrid
              className={classes.tripgridtable}
              getRowId={(row) => row.id}
              rows={dataRows}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              onPageChange={(newPage) => setPage(parseInt(newPage, 10) + 1)}
              rowsPerPageOptions={[5, 10, 25, 50]}
              pagination
              paginationMode="server"
              rowCount={totalRecords}
              autoHeight
              disableSelectionOnClick
              rowHeight={80}
              style={{ padding: "10px" }}
            />
          </section>
        </Grid>
      </Grid>
    </div>
  );
};

export default TriplistPassenger;